<template>
  <div>
    <portal to="aux-modal">
      <CommunicationSchedulerModal v-if="showScheduler" @close="showScheduler = false" @schedule-send="setScheduledAndSend" :preset-date="form.scheduled_at" />
    </portal>
    <ModalBase
      show-modal
      :auto-h="!maximized"
      :width-ms="!maximized"
      :width-xl="maximized"
      :full-h="maximized"
      :loading="sending"
      @close-modal="close"
      class="communication-compose-modal"
      :class="{ maximized: maximized }"
    >
      <template v-slot:titlebar>
        <TitleBar variant-class="title-modal title-modal--simple">
          <template v-slot:title>
            <div class="compose-titlewrap">
              <div class="compose-titlewrap--title">
                {{ $gettext('New message') }}
                {{ titleMessage }}
              </div>
              <font-awesome-icon
                v-if="!maximized"
                icon="fa-solid fa-arrow-up-right-and-arrow-down-left-from-center"
                class="titlebar-maximize"
                @click="maximized = !maximized"
              />
              <font-awesome-icon
                v-else
                icon="fa-solid fa-arrow-down-left-and-arrow-up-right-to-center"
                class="titlebar-maximize"
                @click="maximized = !maximized"
              />
            </div>
          </template>
        </TitleBar>
      </template>
      <template v-slot:maincontent>
        <div v-if="!sending" class="compose-container">
          <!-- communication content -->
          <div class="communication-compose-body">
            <div role="group">
              <div class="recipient-wrap">
                <div class="recipient-label">
                  {{ $gettext('To:') }}
                </div>
                <div style="flex: 1">
                  <CompanySearchInput
                    id="compose-recipients"
                    v-model="form.recipients"
                    :placeholder="sendToAll ? $gettext('All clients selected') : $gettext('Type a company name...')"
                    :disabled="sendToAll"
                  />
                </div>
                <div style="padding-bottom: 4px; display: flex; align-items: flex-end">
                  <b-form-checkbox v-model="sendToAll" switch size="sm">
                    {{ $gettext('All') }}
                  </b-form-checkbox>
                </div>
              </div>
              <div v-if="errors.recipients" style="font-size: 0.875em; margin-top: 0.25rem;">
                <small class="text-danger">{{ errors.recipients }}</small>
              </div>
            </div>
            <hr />
            <div role="group">
              <b-form-input
                v-model="form.subject"
                :state="errors.subject === undefined ? null : false"
                id="notification-subject-input"
                class="communication-control"
                :disabled="sending"
                autocomplete="off"
                :placeholder="$gettext('Subject')"
              />
              <b-form-invalid-feedback v-if="errors.subject" style="margin: 0;">
                <small>{{ errors.subject }}</small>
              </b-form-invalid-feedback>
            </div>
            <hr />
            <div role="group">
              <b-form-textarea
                v-model="form.message"
                :state="errors.message === undefined ? null : false"
                id="notification-message-input"
                class="communication-control"
                :disabled="sending"
                :placeholder="$gettext('Message')"
                autocomplete="off"
                rows="6"
                :max-rows="textareaRows"
                :key="textareaRows"
                no-resize
              />
              <b-form-invalid-feedback v-if="errors.message" style="margin: 0;">
                <small>{{ errors.message }}</small>
              </b-form-invalid-feedback>
            </div>
          </div>

          <!-- options -->
          <hr />
          <b-form-group style="margin: 0">
            <b-form-checkbox :disabled="sending" v-model="form.send_email" switch size="sm">
              {{ $gettext('Send an email') }}
            </b-form-checkbox>
            <b-form-checkbox :disabled="sending" v-model="form.send_push" switch size="sm">
              {{ $gettext('Send a push notification') }}
            </b-form-checkbox>
          </b-form-group>

          <hr />

          <!-- send or schedule -->
          <div class="text-right">
            <b-dropdown
              class="ui-dashboard-menu-dropdown"
              dropup
              split
              variant="primary"
              @click="handleSubmit"
              :disabled="sending || isWaitingForResponse"
              :text="(isEdit) ? $gettext('Save') : $gettext('Send')"
            >
              <b-dropdown-item @click="showScheduler = true">
                <font-awesome-icon icon="fa-kit fa-solid-paper-plane-top-clock" />
                {{ (isEdit) ? $gettext('Edit schedule') : $gettext('Schedule send') }}
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </template>
    </ModalBase>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import ModalBase from '@/components/base_components/ModalBase'
import PremiumModalDefaultContent from '@/components/ui/PremiumModalDefaultContent'
import TitleBar from '@/components/TitleBar'
import InvoiceLines from '@/components/invoices/ui/InvoiceLines'
import LoadingSpinner from '@/components/dashboard/ui/LoadingSpinner'
import PaymentTypeSelector from '@/components/costs/PaymentTypeSelector'
import FileUploader from '@/components/ui/FileUploader'
import ComposeMessage from '@/components/accountants/communication/compose/ComposeMessage'
import CompanySearchInput from '@/components/accountants/communication/CompanySearchInput'
import CommunicationSchedulerModal from '@/components/accountants/communication/compose/CommunicationSchedulerModal'

import CommunicationService from '@/store/services/communication.service'

export default {
  name: 'CommunicationComposeModal',
  components: {
    CompanySearchInput,
    ComposeMessage,
    FileUploader,
    PaymentTypeSelector,
    LoadingSpinner,
    InvoiceLines,
    TitleBar,
    PremiumModalDefaultContent,
    ModalBase,
    CommunicationSchedulerModal,
  },
  props: {
    communication: {
      type: Object,
      required: false,
      default: null,
    },
    presetRecipient: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      form: {
        scheduled_at: new Date(),
        recipients: [],
        subject: '',
        message: '',
        send_email: false,
        send_push: false
      },
      sendToAll: false,
      showScheduler: false,
      sending: false,
      errors: {},
      maximized: false,
      windowHeight: window.innerHeight
    }
  },
  computed: {
    ...mapGetters({
      isWaitingForResponse: 'main/isWaitingForResponse',
    }),
    titleMessage () {
      let message = ''
      if (this.form.recipients && this.form.recipients.length > 0) {
        message = this.$gettext(' to ') + this.form.recipients.map(recipient => recipient.name).join(', ')
      }
      if (this.sendToAll) {
        message = this.$gettext(' to everyone')
      }
      return message
    },
    textareaRows() {
      // base: 20 rows at 747px => 37.35 px/row
      const pxPerRow = 1275 / 38

      // when not maximized, cap at 20 (your current behavior)
      if (!this.maximized) return 20

      // when maximized, scale with viewport height
      const rows = Math.floor(this.windowHeight / pxPerRow)

      // optional clamp
      return Math.max(6, Math.min(rows, 60))
    },
    isEdit () {
      return (this.$route.params['communication_id'])
    }
  },
  watch: {
    communication(newValue, _) {
      this.setCommunication(newValue)
    },
    sendToAll(newValue, oldValue) {
      this.form.recipients = []
    },
    maximized () {

    }
  },
  created () {
    this.setupData()
  },
  mounted() {
    window.addEventListener('resize', this.updateWindowHeight)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateWindowHeight)
  },
  methods: {
    close() {
      this.$emit('close', false)
    },
    setupData() {
      if (this.communication) {
        this.setCommunication(this.communication)
      }
      if (this.presetRecipient) {
        this.form.recipients.push(this.presetRecipient)
      }
    },
    updateWindowHeight() {
      this.windowHeight = window.innerHeight
    },
    
    validateForm() {
      // reset errors
      this.errors = {}
      const trim = v => String(v || '').trim()
      // subject required
      if (!trim(this.form.subject)) {
        this.$set(this.errors, 'subject', this.$gettext('Subject is required'))
      }
      // message required
      if (!trim(this.form.message)) {
        this.$set(this.errors, 'message', this.$gettext('Message is required'))
      }
      // recipients required when NOT sending to all
      if (!this.sendToAll) {
        const hasRecipients = Array.isArray(this.form.recipients) && this.form.recipients.length > 0
        if (!hasRecipients) {
          this.$set(this.errors, 'recipients', this.$gettext('At least one recipient is required'))
        }
      }
      const valid = Object.keys(this.errors).length === 0
      return valid
    },
    handleSubmit() {
      if (!this.validateForm()) {
        this.showScheduler = false
        return
      }
      this.sending = true

      let recipients = []
      if (this.form.recipients && this.form.recipients.length > 0) {
        recipients = this.form.recipients.map((company) => company.id)
      }

      const communication = {
        subject: this.form.subject,
        message: this.form.message,
        recipient_ids: recipients,
        scheduled_at: this.form.scheduled_at,
        send_email: this.form.send_email,
        send_push: this.form.send_push
      }

      if (!this.communication) {
        CommunicationService.createCommunication(communication)
          .then(() => {
            this.$store.commit('setAlert', {
              title: this.$gettext('Communication created'),
              text:  this.$gettext('Your communication was successfully created and scheduled.'),
              variant: 'success',
              duration: 4,
            })
            this.$emit('submit')
            this.close()
          })
          .catch(() => {
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.$gettext('We failed to save your communication, please try again later.'),
              variant: 'danger',
              duration: 4,
            })
          })
          .finally(() => {
            this.sending = false
            this.showScheduler = false
          })
      } else {
        CommunicationService.update(this.communication.id, communication)
          .then(() => {
            this.$store.commit('setAlert', {
              title: this.$gettext('Communication updated'),
              text: this.$gettext('Your communication was successfully updated.'),
              variant: 'success',
              duration: 4,
            })
            this.$emit('submit')
            this.close()
          })
          .catch(() => {
            this.$store.commit('setAlert', {
              title: this.$gettext('Error'),
              text: this.$gettext('We failed to save your communication, please try again later.'),
              variant: 'danger',
              duration: 4,
            })
          })
          .finally(() => {
            this.sending = false
            this.showScheduler = false
          })
      }
    },
    setCommunication(communication) {
      console.log('CommunicationComposeModal::setCommunication()', communication)

      let recipients = []
      if (communication.recipients && communication.recipients.length > 0) {
        recipients = communication.recipients
      } else {
        this.sendToAll = true
      }
      this.form = {
        subject: communication.subject,
        message: communication.message,
        scheduled_at: new Date(communication.scheduled_at),
        recipients: recipients,
        send_email: communication.send_email,
        send_push: communication.send_push
      }
      console.log(this.form)
    },
    setScheduledAndSend (schedule_date) {
      this.form.scheduled_at = schedule_date
      this.handleSubmit()
    }
  },
}
</script>

<style lang="scss">
.communication-compose-modal {
  .gk-modal {
    padding: 0rem;
    &.maximized {
      min-height: 100% !important;
    }
    .gk-modal-content {
      display: block;
      .close-btn {
        top: 6px;
        right: 4px;
        background: transparent !important;
        color: #041e49 !important;
        &:hover {
          background: transparent !important;
        }
      }
    }
  }
  .titlebar.title-modal--simple {
    position: relative !important;
    margin-bottom: 0px !important;
    padding: 0 1rem !important;
    background: #f2f6fc !important;
    border: none !important;
    h2 {
      font-size: 16px !important;
      color: #041e49 !important;
      font-family: $fontRegular !important;
      width: calc(100% - 1.5rem) !important;
    }
    .title-wrap {
      min-height: 40px !important;
    }
    .title-wrap,
    .pagetitle,
    .pagetitle--main {
      width: 100%;
    }
    .compose-titlewrap {
      display: flex;
      align-items: center;
      justify-content: space-between;
      font-size: 1rem;
      &--title {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 95%;
      }
    }
    .titlebar-maximize {
      cursor: pointer;
    }
  }
  .modal-content-wrap {
    height: calc(100% - 40px);
    min-height: 500px;
    padding: 1rem;
    display: flex;
    &:not(.has-actions) {
      border-radius: 0px 0px $radiusLg $radiusLg;
    }
  }
}

.communication-control {
  border: none !important;
  box-shadow: none !important;
  border-radius: 0 !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  font-size: 1rem !important;
  background: white !important;
}
textarea.communication-control {
  overflow: auto;
}
.compose-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  font-size: 17px;
  hr { width: 100%; }
  .communication-compose-body {
    height: 100%;
    hr {
      margin-top: 0.25rem;
      margin-bottom: 0.25rem;
    }
    .recipient-wrap {
      display: flex;
      gap: 0.5rem;
      .recipient-label {
        font-size: 1rem;
        display: flex;
        align-items: center;
      }
    }
  }
}
</style>